import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';
import {
  Users, MessageCircle, Search, X, ChevronRight,
  UserPlus, Share2, Trophy, Swords, Plus, Check, Zap
} from 'lucide-react';

// ── Helpers ──────────────────────────────────────────────────────
const getSportEmoji = (category = '') => {
  const c = (category || '').toLowerCase();
  if (c.includes('soccer')) return '⚽';
  if (c.includes('basketball') || c.includes('nba')) return '🏀';
  if (c.includes('baseball') || c.includes('mlb')) return '⚾';
  if (c.includes('hockey') || c.includes('nhl')) return '🏒';
  if (c.includes('tennis')) return '🎾';
  if (c.includes('volleyball')) return '🏐';
  if (c.includes('rugby')) return '🏉';
  if (c.includes('golf')) return '⛳';
  if (c.includes('football') || c.includes('nfl')) return '🏈';
  return '⚔️';
};

const TEAM_PALETTE = [
  '#EF4444','#F97316','#EAB308','#22C55E','#14B8A6',
  '#3B82F6','#8B5CF6','#EC4899','#06B6D4','#7DD3FC',
  '#F43F5E','#10B981','#0EA5E9','#FBBF24','#0EA5E9',
];
const hashColor = (str = '') => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return TEAM_PALETTE[Math.abs(h) % TEAM_PALETTE.length];
};

const initials = (name = '') => {
  const w = name.trim().split(/\s+/);
  return w.length >= 2 ? `${w[0][0]}${w[1][0]}`.toUpperCase() : name.substring(0, 2).toUpperCase();
};

const msgText = (m) => m?.comment ?? m?.text ?? m?.message ?? '';
const msgAuthor = (m) => m?.sender?.username ?? (typeof m?.sender === 'string' ? m.sender : null) ?? m?.user?.username ?? m?.username ?? 'Fan';

// ── Team card ────────────────────────────────────────────────────
function TeamCard({ team, onSelect }) {
  const color = hashColor(team.name || '');
  const emoji = getSportEmoji(team.category_sport || '');
  return (
    <div className="team-card" onClick={() => onSelect(team)}
      style={{ '--team-color': color }}>
      <div className="team-card-stripe" style={{ background: `linear-gradient(90deg, ${color}, ${color}66)` }} />
      <div className="team-card-body">
        <div className="team-card-logo" style={{ background: `${color}22`, borderColor: `${color}55` }}>
          <span style={{ fontSize: 28 }}>{emoji}</span>
        </div>
        <div className="team-card-info">
          <span className="team-card-sport">{(team.category_sport || 'RIVALRY').toUpperCase()}</span>
          <h3 className="team-card-name">{team.name}</h3>
          {team.rival_name && (
            <span className="team-card-rival">vs {team.rival_name}</span>
          )}
        </div>
        <div className="team-card-meta">
          <span className="team-card-members">
            <Users size={12} /> {team.participants_count || 0} fans
          </span>
          <ChevronRight size={16} color="rgba(148,163,184,0.4)" />
        </div>
      </div>
    </div>
  );
}

// ── Team detail panel ────────────────────────────────────────────
function TeamDetail({ team, token, user, onClose }) {
  const [activeTab, setActiveTab] = useState('About');
  const [isMember, setIsMember] = useState(false);
  const [isRivalMember, setIsRivalMember] = useState(false);
  const [joining, setJoining] = useState(false);
  const [members, setMembers] = useState(team?.participants || []);
  const [messages, setMessages] = useState([]);
  const [msgInput, setMsgInput] = useState('');
  const [sending, setSending] = useState(false);
  const [follows, setFollows] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [followLoading, setFollowLoading] = useState({});
  const [showSuggest, setShowSuggest] = useState(false);
  const wsRef = useRef(null);
  const chatBottomRef = useRef(null);
  const color = hashColor(team.name || '');
  const emoji = getSportEmoji(team.category_sport || '');

  // Membership + messages + follows
  useEffect(() => {
    const load = async () => {
      if (!token) return;
      try {
        const [memStatus, rivalStatus, msgs, fws, detail] = await Promise.all([
          api.getMembershipStatus(token, team.id).catch(() => ({})),
          team.rival_fan_zone ? api.getMembershipStatus(token, team.rival_fan_zone).catch(() => ({})) : Promise.resolve({}),
          api.getFanzoneMessages(token, team.id).catch(() => []),
          api.getMyFollows(token).catch(() => []),
          api.getFanzoneDetail(token, team.id).catch(() => null),
        ]);
        setIsMember(!!memStatus?.is_member);
        setIsRivalMember(!!rivalStatus?.is_member);
        const msgList = Array.isArray(msgs) ? msgs : msgs?.results ?? [];
        setMessages(msgList);
        const followList = Array.isArray(fws) ? fws : fws?.results ?? [];
        const myId = user?.id;
        const iFollow = new Set(
          followList.filter(e => (e?.follower?.id ?? e?.follower) === myId).map(e => e?.followed?.id ?? e?.followed)
        );
        setFollows([...iFollow]);
        if (detail?.participants) setMembers(detail.participants);
      } catch {}
    };
    load();
  }, [team.id, token, user?.id, team.rival_fan_zone]);

  // WebSocket for live chat
  useEffect(() => {
    if (!token) return;
    let ws;
    try {
      ws = new WebSocket(`wss://chat.meanc.net/ws/fanzone/${team.id}/?token=${token}`);
      wsRef.current = ws;
      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.type === 'fanzone_message' && data.message) {
            const msg = data.message;
            setMessages(prev => {
              if (prev.some(c => c.id === msg.id)) return prev;
              const clean = prev.filter(c => !(String(c.id).startsWith('opt_') && msgText(c) === msgText(msg)));
              return [...clean, msg];
            });
          }
        } catch {}
      };
    } catch {}
    return () => { ws?.close(); wsRef.current = null; };
  }, [team.id, token]);

  // Scroll chat to bottom
  useEffect(() => {
    if (activeTab === 'Chat') chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, activeTab]);

  const handleJoin = async () => {
    if (isMember || joining || !token) return;
    if (isRivalMember) { alert('Leave the rival fan zone first before joining this one.'); return; }
    setJoining(true);
    try {
      const res = await api.joinFanzone(token, team.id);
      if (res?.detail || res?.id || res?.status) setIsMember(true);
    } catch {}
    setJoining(false);
  };

  const handleSend = async () => {
    const text = msgInput.trim();
    if (!text || !isMember || !token) return;
    setSending(true);
    const optId = `opt_${Date.now()}`;
    const optMsg = { id: optId, message: text, sender: { username: user?.username || 'You' }, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, optMsg]);
    setMsgInput('');
    try {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ update_type: 'send_message', message: text, user_id: user?.id }));
      } else {
        const saved = await api.postFanzoneMessage(token, team.id, text);
        setMessages(prev => prev.map(m => m.id === optId ? (saved || m) : m));
      }
    } catch {
      setMessages(prev => prev.filter(m => m.id !== optId));
    }
    setSending(false);
  };

  const handleFollow = async (member) => {
    if (!token || followLoading[member.id]) return;
    setFollowLoading(p => ({ ...p, [member.id]: true }));
    try {
      if (follows.includes(member.id)) {
        await api.unfollowUser(token, member.id);
        setFollows(p => p.filter(id => id !== member.id));
      } else {
        await api.followUser(token, member.id);
        setFollows(p => [...p, member.id]);
      }
    } catch {}
    setFollowLoading(p => ({ ...p, [member.id]: false }));
  };

  return (
    <div className="team-detail-overlay" onClick={onClose}>
      <div className="team-detail-panel" onClick={e => e.stopPropagation()}>
        {/* Header stripe */}
        <div className="td-stripe" style={{ background: `linear-gradient(90deg, ${color}, ${color}55)` }} />

        {/* Hero */}
        <div className="td-hero" style={{ background: `linear-gradient(135deg, ${color}22 0%, #0D1117 60%, ${color}11 100%)` }}>
          <button className="td-close" onClick={onClose}><X size={18} /></button>
          <div className="td-hero-inner">
            <div className="td-logo" style={{ background: `${color}22`, borderColor: `${color}55` }}>
              <span style={{ fontSize: 36 }}>{emoji}</span>
            </div>
            <div className="td-hero-text">
              <span className="td-sport">{(team.category_sport || 'FAN ZONE').toUpperCase()}</span>
              <h2 className="td-name">{team.name}</h2>
              {team.rival_name && <span className="td-rival">vs {team.rival_name}</span>}
            </div>
            <button
              className={`td-join-btn ${isMember ? 'td-join-btn--joined' : ''}`}
              onClick={handleJoin}
              disabled={isMember || joining || isRivalMember}
            >
              {isMember ? <><Check size={14} /> Joined</> : joining ? 'Joining…' : isRivalMember ? 'Rival fan' : <><Plus size={14} /> Join</>}
            </button>
          </div>
          <div className="td-stats">
            <div className="td-stat"><span className="td-stat-val">{members.length || team.participants_count || 0}</span><span className="td-stat-lbl">Fans</span></div>
            <div className="td-stat-div" />
            <div className="td-stat"><span className="td-stat-val">{messages.length}</span><span className="td-stat-lbl">Messages</span></div>
            <div className="td-stat-div" />
            <div className="td-stat"><span className="td-live-dot" /><span className="td-stat-lbl">Live</span></div>
          </div>
        </div>

        {/* Tabs */}
        <div className="td-tabs">
          {['About', 'Chat', 'Members'].map(t => (
            <button key={t} className={`td-tab ${activeTab === t ? 'active' : ''}`} onClick={() => setActiveTab(t)}>
              {t === 'Chat' && <span className="td-live-dot" style={{ marginRight: 4 }} />}
              {t}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="td-content">
          {activeTab === 'About' && (
            <div className="td-about">
              <div className="td-action-grid">
                <button className="td-action-card" style={{ borderColor: 'rgba(14,165,233,0.4)', background: 'rgba(14,165,233,0.08)' }}
                  onClick={() => setActiveTab('Chat')}>
                  <MessageCircle size={22} color="#0EA5E9" />
                  <span className="td-action-label">Fan Chat</span>
                  <span className="td-action-sub">Post a thought</span>
                </button>
                <button className="td-action-card" style={{ borderColor: 'rgba(34,211,238,0.3)', background: 'rgba(34,211,238,0.06)' }}
                  onClick={() => setActiveTab('Members')}>
                  <UserPlus size={22} color="#22D3EE" />
                  <span className="td-action-label">Members</span>
                  <span className="td-action-sub">View & follow fans</span>
                </button>
              </div>
              <button className="td-invite-btn" onClick={() => {
                if (navigator.share) navigator.share({ text: `📣 I'm backing ${team.name} on MEANC! Join and pick a side 🔥` });
              }}>
                <Share2 size={16} /> Invite Friends to Back {team.name}
              </button>
              <div className="td-crew-section">
                <span className="td-section-label">CREW MEMBERS</span>
                <div className="td-avatar-row" onClick={() => setActiveTab('Members')}>
                  {members.slice(0, 8).map((m, i) => {
                    const n = m.username || m.user?.username || 'F';
                    return (
                      <div key={m.id || i} className="td-avatar" style={{ marginLeft: i > 0 ? -10 : 0, zIndex: 10 - i, background: hashColor(n) }}>
                        {n[0]?.toUpperCase()}
                      </div>
                    );
                  })}
                  {(members.length || team.participants_count || 0) > 8 && (
                    <div className="td-avatar td-avatar-more" style={{ marginLeft: -10 }}>
                      +{(members.length || team.participants_count) - 8}
                    </div>
                  )}
                  {members.length === 0 && <span style={{ color: 'rgba(148,163,184,0.6)', fontSize: 13 }}>No members yet — be the first!</span>}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'Chat' && (
            <div className="td-chat">
              {!isMember ? (
                <div className="td-locked">
                  <MessageCircle size={44} color="rgba(14,165,233,0.3)" />
                  <h4>Members Only Chat</h4>
                  <p>Join this fan zone to read and send messages with the crew.</p>
                  <button className="td-join-big" onClick={handleJoin} disabled={joining || isRivalMember}>
                    {joining ? 'Joining…' : isRivalMember ? 'You\'re in the rival zone' : '🏟 Join & Chat'}
                  </button>
                </div>
              ) : (
                <>
                  <div className="td-live-row">
                    <span className="td-live-dot" />
                    <span>{messages.length} messages in fan chat</span>
                  </div>
                  <div className="td-messages">
                    {messages.length === 0 ? (
                      <div className="td-empty-chat">
                        <MessageCircle size={36} color="rgba(14,165,233,0.3)" />
                        <p>No messages yet — rally the crew!</p>
                      </div>
                    ) : (
                      messages.map((m, i) => (
                        <div key={m.id || i} className="td-msg">
                          <div className="td-msg-avatar" style={{ background: hashColor(msgAuthor(m)) }}>
                            {(msgAuthor(m)[0] || '?').toUpperCase()}
                          </div>
                          <div className="td-msg-body">
                            <span className="td-msg-author">{msgAuthor(m)}</span>
                            <span className="td-msg-text">{msgText(m)}</span>
                          </div>
                        </div>
                      ))
                    )}
                    <div ref={chatBottomRef} />
                  </div>
                  <div className="td-chat-input-row">
                    <input
                      className="td-chat-input"
                      placeholder="Say something to the crew…"
                      value={msgInput}
                      onChange={e => setMsgInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
                      maxLength={280}
                    />
                    <button className="td-send-btn" onClick={handleSend} disabled={!msgInput.trim() || sending}>
                      {sending ? '…' : 'Send'}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'Members' && (
            <div className="td-members">
              {members.length === 0 ? (
                <div className="td-empty-chat">
                  <Users size={40} color="rgba(14,165,233,0.3)" />
                  <p>No members yet — join and be first!</p>
                </div>
              ) : (
                members.map((m, i) => {
                  const name = m.username || m.user?.username || 'Fan';
                  const isSelf = m.id === user?.id;
                  const isFollowing = follows.includes(m.id);
                  return (
                    <div key={m.id || i} className="td-member-row" onClick={() => setSelectedMember(m)}>
                      <div className="td-member-avatar" style={{ background: hashColor(name) }}>
                        {name[0]?.toUpperCase()}
                      </div>
                      <div className="td-member-info">
                        <span className="td-member-name">{name}</span>
                        <span className="td-member-meta">{isSelf ? '👤 You' : isFollowing ? '🤝 Following' : '🏟 Fan'}</span>
                      </div>
                      {!isSelf && (
                        <button
                          className={`td-follow-btn ${isFollowing ? 'td-follow-btn--following' : ''}`}
                          onClick={e => { e.stopPropagation(); handleFollow(m); }}
                          disabled={followLoading[m.id]}
                        >
                          {followLoading[m.id] ? '…' : isFollowing ? 'Unfollow' : '+ Follow'}
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>

      {/* Member profile modal */}
      {selectedMember && (
        <div className="member-profile-overlay" onClick={() => setSelectedMember(null)}>
          <div className="member-profile-card" onClick={e => e.stopPropagation()}>
            <button className="td-close" style={{ position: 'absolute', top: 12, right: 12 }} onClick={() => setSelectedMember(null)}>
              <X size={16} />
            </button>
            {(() => {
              const m = selectedMember;
              const name = m.username || m.user?.username || 'Fan';
              const isSelf = m.id === user?.id;
              const isFollowing = follows.includes(m.id);
              return (
                <>
                  <div className="mp-avatar" style={{ background: `linear-gradient(135deg, ${hashColor(name)}, ${hashColor(name)}88)` }}>
                    {name[0]?.toUpperCase()}
                  </div>
                  <h3 className="mp-name">{name}</h3>
                  <span className="mp-meta">{isSelf ? '👤 You' : isFollowing ? '🤝 Mutual follow' : '🏟 Fan'}</span>
                  {!isSelf && (
                    <button
                      className={`mp-follow-btn ${isFollowing ? 'mp-follow-btn--following' : ''}`}
                      onClick={() => handleFollow(m)}
                      disabled={followLoading[m.id]}
                    >
                      {followLoading[m.id] ? '…' : isFollowing ? 'Unfollow' : '+ Follow'}
                    </button>
                  )}
                  <button className="mp-close-link" onClick={() => setSelectedMember(null)}>Close</button>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Teams Page ──────────────────────────────────────────────
const SPORTS = ['All', 'Football', 'Soccer', 'Basketball', 'Baseball', 'Hockey', 'Tennis'];

export default function TeamsPage() {
  const { token, user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sport, setSport] = useState('All');
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showSuggest, setShowSuggest] = useState(false);
  const [suggestion, setSuggestion] = useState({ name: '', sport: 'Football', description: '' });
  const [suggesting, setSuggesting] = useState(false);
  const [suggestDone, setSuggestDone] = useState(false);

  const fetchTeams = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError('');
    try {
      const data = await api.getFanzones(token);
      const list = Array.isArray(data) ? data : data?.results ?? [];
      setTeams(list);
    } catch {
      setError('Could not load teams.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchTeams(); }, [fetchTeams]);

  const filtered = useMemo(() => teams.filter(t => {
    const name = `${t.name || ''} ${t.category_sport || ''}`.toLowerCase();
    if (search && !name.includes(search.toLowerCase())) return false;
    if (sport !== 'All' && t.category_sport !== sport) return false;
    return true;
  }), [teams, search, sport]);

  const sports = useMemo(() => {
    const s = new Set(teams.map(t => t.category_sport).filter(Boolean));
    return ['All', ...Array.from(s)];
  }, [teams]);

  const handleSuggest = async () => {
    if (!token || !suggestion.name.trim()) return;
    setSuggesting(true);
    try {
      await api.suggestTeam(token, { name: suggestion.name.trim(), category_sport: suggestion.sport, description: suggestion.description.trim() });
      setSuggestDone(true);
      setTimeout(() => { setShowSuggest(false); setSuggestDone(false); setSuggestion({ name: '', sport: 'Football', description: '' }); }, 2000);
    } catch {}
    setSuggesting(false);
  };

  return (
    <div className="teams-page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title"><Trophy size={24} className="page-title-icon" />Teams Hub</h1>
          <span className="teams-count-badge">{teams.length} teams</span>
        </div>
        <button className="td-join-big" style={{ width: 'auto', padding: '8px 16px', fontSize: 13 }} onClick={() => setShowSuggest(true)}>
          <Plus size={15} /> Suggest Team
        </button>
      </div>

      {/* Hero stats */}
      <div className="teams-hero-stats">
        <div className="ths-item">
          <span className="ths-num">{teams.length}</span>
          <span className="ths-lbl">Total Teams</span>
        </div>
        <div className="ths-divider" />
        <div className="ths-item">
          <span className="ths-num">{sports.length - 1}</span>
          <span className="ths-lbl">Sports</span>
        </div>
        <div className="ths-divider" />
        <div className="ths-item">
          <span className="ths-num">{filtered.length}</span>
          <span className="ths-lbl">Showing</span>
        </div>
      </div>

      {/* Search */}
      <div className="search-wrap" style={{ marginBottom: 16 }}>
        <Search size={15} color="#64748B" className="search-icon" />
        <input className="search-input" placeholder="Search teams, sports…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {/* Sport filter */}
      <div className="teams-sport-tabs">
        {sports.map(s => (
          <button key={s} className={`teams-sport-tab ${sport === s ? 'active' : ''}`} onClick={() => setSport(s)}>
            {s !== 'All' && getSportEmoji(s)} {s}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="teams-skeleton-grid">
          {[1,2,3,4,5,6].map(i => (
            <div key={i} className="teams-skeleton">
              <div className="sk-stripe" /><div className="sk-circle" /><div className="sk-lines"><div className="sk-line wide" /><div className="sk-line narrow" /></div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="feed-error">
          <Trophy size={40} color="rgba(239,68,68,0.4)" />
          <p>{error}</p>
          <button className="auth-btn" onClick={fetchTeams} style={{ width: 'auto', padding: '10px 24px' }}>Retry</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="feed-empty">
          <Trophy size={48} color="rgba(14,165,233,0.3)" />
          <p>No teams found</p>
          <span>Try a different filter or suggest a new team.</span>
        </div>
      ) : (
        <div className="teams-grid">
          {filtered.map(t => <TeamCard key={t.id} team={t} onSelect={setSelectedTeam} />)}
        </div>
      )}

      {/* Team detail panel */}
      {selectedTeam && (
        <TeamDetail team={selectedTeam} token={token} user={user} onClose={() => setSelectedTeam(null)} />
      )}

      {/* Suggest team modal */}
      {showSuggest && (
        <div className="modal-overlay" onClick={() => setShowSuggest(false)}>
          <div className="suggest-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={() => setShowSuggest(false)}><X size={18} /></button>
            <div className="suggest-icon"><Swords size={28} color="#0EA5E9" /></div>
            <h3>Suggest a Team</h3>
            <p>Name the team — we'll make it happen.</p>
            {suggestDone ? (
              <div style={{ textAlign: 'center', color: '#22C55E', fontWeight: 700, padding: '16px 0' }}>✓ Suggestion submitted! Thanks.</div>
            ) : (
              <>
                <input className="suggest-input" placeholder="Team name" value={suggestion.name} onChange={e => setSuggestion(p => ({ ...p, name: e.target.value }))} />
                <select className="suggest-input" value={suggestion.sport} onChange={e => setSuggestion(p => ({ ...p, sport: e.target.value }))}>
                  {SPORTS.slice(1).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <textarea className="suggest-input" placeholder="Description (optional)" rows={3} value={suggestion.description} onChange={e => setSuggestion(p => ({ ...p, description: e.target.value }))} style={{ resize: 'none', height: 'auto' }} />
                <button className="td-join-big" onClick={handleSuggest} disabled={!suggestion.name.trim() || suggesting}>
                  {suggesting ? 'Submitting…' : '⚔️ Submit Suggestion'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
